import { computed, type ComputedRef } from 'vue';
import { useDisplay } from 'vuetify';

/// Reactive "phone-sized viewport" signal.  Backed by Vuetify's
/// `smAndDown` breakpoint (viewport width < 960 px) so the layout
/// responds to browser resize and device rotation without depending
/// on a User-Agent string — UA sniffing gets fooled by foldables,
/// tablets in desktop mode, and every DevTools spoofing scenario.
///
/// Two escape hatches for testing on a real desktop:
///   • `?mobile=1` in the URL forces mobile.  `?mobile=0` forces
///     desktop.  Higher priority than the localStorage override so
///     you can drop the URL param and reload without stale state.
///   • `localStorage.chorus.forceMobile = "1" | "0"` — persists across
///     page reloads so QA runs don't have to re-append the query
///     string every time.
///
/// The tablet cohort (960–1279 px) stays on the desktop layout by
/// design — REPMUS operators on iPads in landscape want the full
/// sidebar view, not the phone layout.
export function useIsMobile(): ComputedRef<boolean> {
    const display = useDisplay();

    const override = computed<boolean | null>(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const q = params.get('mobile');
            if (q === '1') return true;
            if (q === '0') return false;
            const ls = localStorage.getItem('chorus.forceMobile');
            if (ls === '1') return true;
            if (ls === '0') return false;
        } catch {
            // localStorage / URLSearchParams throw in restricted contexts
            // (private-mode iframe, SSR). Ignore and fall through to
            // viewport detection.
        }
        return null;
    });

    return computed(() => override.value ?? display.smAndDown.value);
}
